import type {
  Env,
  Region,
  RowState,
} from '@internal/backstage-plugin-bidone-backend';
import type { BDirectRow } from '../../../hooks/useBDirectRows';

// Canonical column order for the BDirect table. Mirrors `REGIONS` exported
// from the backend domain package — duplicated here as a runtime value so
// the frontend bundle doesn't reach into the backend module (which imports
// Node's `crypto` for `randomUUID`).
export const REGION_COLUMNS: readonly Region[] = [
  'AUE',
  'WEU',
  'SAF',
  'BRS',
  'ASE',
  'NEU',
] as const;

// Subset of regions surfaced as read-only PREPROD columns immediately
// before the primary region columns. PREPROD only deploys to AUE / WEU,
// so the rest of the regions stay collapsed under the regular columns.
export const PREPROD_REGION_COLUMNS: readonly Region[] = ['AUE', 'WEU'] as const;

export interface CommonProps {
  release: string;
  env: Env;
  section: BDirectRow;
  steps: BDirectRow[];
  // Per-row state mutation. Fires immediately on dropdown change —
  // independent of the section's Edit lock, per spec.
  onChangeState: (rowId: string, next: RowState) => void;
}

export interface ViewProps extends CommonProps {
  mode: 'view';
  // When the section is currently locked by someone other than the viewer
  // we show a banner + the Edit button is hidden.
  lockedBy: string | null;
  currentUser: string | null;
  onEdit: () => void;
  editPending: boolean;
  // True when another section in the same release is already being edited
  // by this user. We restrict editing to one section at a time so the user
  // never has multiple in-flight drafts to reconcile.
  editDisabled?: boolean;
}

export interface EditProps extends CommonProps {
  mode: 'edit';
  // Draft section title (parent-owned, dispatched via onUpdateSectionText).
  sectionText: string;
  onUpdateSectionText: (next: string) => void;
  // Section-level actions.
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  savePending: boolean;
  cancelPending: boolean;
  deletePending: boolean;
  // Step-level mutations.
  onOpenStepEditor: (stepId: string) => void;
  onMoveStep: (stepId: string, dir: -1 | 1) => void;
  onRemoveStep: (stepId: string) => void;
  onAddManualStep: () => void;
  onToggleRegion: (
    stepId: string,
    region: Region,
    env: Env,
    value: boolean,
  ) => void;
}

export type SectionCardProps = ViewProps | EditProps;
